import { ArrowForward, Send } from '@mui/icons-material';
import { Stack, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  StepperWizard,
  GenCurrency,
  GenSummaryCurrencyDetail,
} from '@pinnacleautomotive/clarity-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { NewOrderTypes } from '../../../common/enums/enums';
import UnlinkedChip from '../../../components/UnlinkedChip';
import { getServiceOrderSelectedServicesPricesTotalValue } from '../../../redux/selectors/orders/newServiceOrderSelectors';
import { getSelectorActionsBasedOnType } from './helper';
import CompletionTime from './steps/CompletionTime';
import Extras from './steps/Extras';
import Services from './steps/Services';
import Vehicle from './steps/Vehicle';
import Vendor from './steps/Vendor';

const StyledBox = styled(Box)(({ theme }) => ({
  maxWidth: '250px',
}));

export default function Stepper({
  onToggleMode,
  onSave,
  currentStep,
  orderType = NewOrderTypes.SIMPLE_ORDER,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['orders', 'global']);
  const [disabled, setDisabled] = useState(true);
  const {
    clearOrderAction,
    isLoadingSelector,
    selectedPricesWithDetailsSelector,
    selectedVehicleSelector,
    selectedVendorSelector,
    specialRequestsSelector,
    choosenAlternativeOwner,
  } = getSelectorActionsBasedOnType(orderType);
  const selectedVendor = useSelector(selectedVendorSelector);
  const selectedPrices = useSelector(selectedPricesWithDetailsSelector) || [];
  const servicesTotal =
    useSelector(getServiceOrderSelectedServicesPricesTotalValue) || 0;
  const selectedVehicle = useSelector(selectedVehicleSelector);
  const isLoading = useSelector(isLoadingSelector);
  const specialRequests = useSelector(specialRequestsSelector);
  const selectedOwner = useSelector(choosenAlternativeOwner);

  const history = useHistory();

  const selectedVendorSummary = (
    <Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body1">
          {selectedVendor?.rootCompany?.name}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body1" color="text.secondary">
          {selectedVendor?.name}
        </Typography>
        {selectedVendor?.active === false && <UnlinkedChip />}
      </Stack>
    </Stack>
  );

  const getServicesSummary = () => ({
    data: [
      {
        name: t('newOrder.steps.vendor'),
        data: [
          {
            name: selectedVendorSummary,
            value: null,
          },
        ],
      },
    ],
  });

  const getServiceOrderVehicleSummary = () => ({
    data: [
      ...getServicesSummary().data,
      {
        name: t('newOrder.steps.services'),
        data: selectedPrices.map(price => ({
          name: <StyledBox>{price.service.name}</StyledBox>,
          value: (
            <StyledBox>
              <GenCurrency value={price.price} /> x {price.quantity}
            </StyledBox>
          ),
        })),
      },
    ],
    footer: (
      <GenSummaryCurrencyDetail
        text={t('total', { ns: 'global' })}
        value={servicesTotal}
      />
    ),
  });

  const getVehicleSummary = () => ({
    data: [
      ...getServicesSummary().data,
      {
        name: t('newOrder.steps.services'),
        data: selectedPrices.map(price => ({
          name: price.service.name,
          value: <GenCurrency value={price.price} />,
        })),
      },
    ],
    footer: (
      <GenSummaryCurrencyDetail
        text={t('total', { ns: 'global' })}
        value={selectedPrices.reduce(
          (total, actual) => total + actual.price,
          0
        )}
      />
    ),
  });

  const getExtrasSummmary = () => {
    const vehicleSummary =
      orderType === NewOrderTypes.SIMPLE_ORDER
        ? getVehicleSummary()
        : getServiceOrderVehicleSummary();
    return {
      data: [
        ...vehicleSummary.data,
        {
          name: t('newOrder.steps.vehicle'),
          data: [
            {
              name: `${t('vin', { ns: 'global' })} #`,
              value: selectedVehicle?.vin,
            },
            {
              name: t('stock', { ns: 'global' }),
              value: selectedVehicle?.custody?.stockNumber || '-',
            },
            {
              name: t('make', { ns: 'global' }),
              value: selectedVehicle?.make,
            },
            {
              name: t('model', { ns: 'global' }),
              value: selectedVehicle?.model,
            },
            {
              name: t('color', { ns: 'global' }),
              value: selectedVehicle?.color || '-',
            },
            {
              name: t('year', { ns: 'global' }),
              value: selectedVehicle?.year,
            },
            {
              name: t('po', { ns: 'global' }),
              value: selectedVehicle?.po || '-',
            },
            {
              name: t('ro', { ns: 'global' }),
              value: selectedVehicle?.ro || '-',
            },
          ],
        },
        {
          name: t('newOrder.steps.extras'),
          data: [
            {
              name: t('newOrder.steps.specialRequest'),
              value:
                specialRequests?.length > 0 ? specialRequests?.length : '-',
            },
            {
              name: t('newOrder.steps.newOwner'),
              value:
                selectedOwner.label && selectedOwner.label !== 'None'
                  ? selectedOwner.label
                  : '-',
            },
          ],
        },
      ],
      footer: vehicleSummary.footer,
    };
  };

  const handleChangeValid = valid => setDisabled(!valid);

  const steps = [
    {
      label: t('newOrder.steps.vendor'),
      component: (
        <Vendor
          onValidStep={handleChangeValid}
          onToggle={onToggleMode}
          orderType={orderType}
        />
      ),
      summary: {
        data: [],
      },
      buttonsIntoSummary: true,
    },
    {
      label: t('newOrder.steps.services'),
      component: (
        <Services onValidStep={handleChangeValid} orderType={orderType} />
      ),
      summary: getServicesSummary(),
      buttonsIntoSummary: true,
    },
    {
      label: t('newOrder.steps.vehicle'),
      component: (
        <Vehicle onValidStep={handleChangeValid} orderType={orderType} />
      ),
      summary:
        orderType === NewOrderTypes.SIMPLE_ORDER
          ? getVehicleSummary()
          : getServiceOrderVehicleSummary(),
      buttonsIntoSummary: true,
    },
    {
      label: t('newOrder.steps.extras'),
      component: <Extras orderType={orderType} />,
      summary: getExtrasSummmary(),
      buttonsIntoSummary: true,
    },
    {
      label: t('newOrder.steps.completionTime'),
      component: (
        <CompletionTime onValidStep={handleChangeValid} orderType={orderType} />
      ),
      summary: getExtrasSummmary(),
      buttonsIntoSummary: true,
    },
  ];

  const defaultButtons = {
    position: 'end',
    columns: 12,
    prev: {
      text: t('genericButtons.back', { ns: 'global' }),
      variant: 'outlined',
      sx: {
        color: 'black',
        borderColor: 'lightgray',
        '&:hover': {
          backgroundColor: 'lightgray',
          borderColor: 'lightgray',
        },
      },
    },
    cancel: {
      text: t('genericButtons.cancel', { ns: 'global' }),
      variant: 'outlined',
      sx: {
        color: theme => theme.palette.text.primary,
        borderColor: theme => theme.palette.divider,
      },
    },
    next: {
      default: {
        text: t('genericButtons.next', { ns: 'global' }),
        icon: <ArrowForward />,
        variant: 'contained',
        color: 'primary',
      },
      isFinalStep: {
        text: t('newOrder.createOrder'),
        icon: <Send />,
        variant: 'contained',
        color: 'success',
        function: onSave,
      },
    },
  };

  const goToWorkOrder = () => {
    dispatch(clearOrderAction());
    history.goBack();
  };

  return (
    <StepperWizard
      steps={steps}
      disabled={disabled}
      loading={isLoading}
      currentStep={currentStep}
      defaultButtons={defaultButtons}
      onCancelFirstStep={() => goToWorkOrder()}
    />
  );
}
